import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import { IEvent, IShow } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import { apiService } from "src/services/api.service";

import Input from "src/components/Input/Input";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Checkbox from "src/components/Checkbox/Checkbox";
import DatePick from "src/components/DatePick/DatePick";

import styles from "./ShowNameTab.module.scss";
import { helperService } from "../../../../services/helper.service";

interface IProps {
  event: IEvent,
  show?: IShow,
  showType: string,
  clubId: string | undefined,
  onChange(akcNum: string, showName: string, startDate: any, closeDate: any, useCloseDate: boolean, isTrailingShow: boolean, previousShow: IShow | undefined, customShowName: string, isFunMatch: boolean): void
}

enum showNameTabErrors {
  NOT_FOUND,
  NO_CLUB
}

const ShowNameTab: React.FC<IProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(false);

  const [akcNum, setAkcNum] = useState<string>("");
  const [showName, setShowName] = useState<IOption>({ value: "Select a Show Name", id: "Select a Show Name" });
  const [customShowName, setCustomShowName] = useState<string>("");
  const [isTrailingShow, setIsTrailingShow] = useState<boolean>(false);
  const [previousShow, setPreviousShow] = useState<IOption>({ value: "Select a Previous Show", id: "Select a Previous Show" });
  const [actualPreviousShow, setActualPreviousShow] = useState<IShow>();
  const [eventShows, setEventShows] = useState<IShow[]>([]);
  const [useCloseDate, setUseCloseDate] = useState<boolean>(false);
  const [isFunMatch, setIsFunMatch] = useState<boolean>(props.show?.is_fun_match ?? false);

  const showFunMatchOption =
    props.event?.sanctioning_club === Constants.sanctioning_club.AKC &&
    (props.showType === Constants.show_type.conformation ||
      props.showType === Constants.show_type.scentwork ||
      props.showType === Constants.show_type.rally_obedience);

  let sDate = new Date(Date.now());
  sDate.setMinutes(0);
  sDate.setHours(9);
  const [showDate, setShowDate] = useState<Date>(sDate);

  const cDate = new Date();
  cDate.setMinutes(0);
  const [closeDate, setCloseDate] = useState<Date>(cDate);

  const getShowNamesByType = (sanctioningClub: string, showType: string) => {
    if (sanctioningClub === Constants.sanctioning_club.AKC && (showType === Constants.show_type.fast_cat || showType === Constants.show_type.cat || showType === Constants.show_type.fetch || showType === Constants.show_type.trick_dog)) {
      return [
        // { id: Constants.show_name.trial_1, value: Constants.show_name.trial_1 },
        // { id: Constants.show_name.trial_2, value: Constants.show_name.trial_2 },
        // { id: Constants.show_name.trial_3, value: Constants.show_name.trial_3 },
        // { id: Constants.show_name.trial_4, value: Constants.show_name.trial_4 }
        { id: Constants.show_name.test_1, value: Constants.show_name.test_1 },
        { id: Constants.show_name.test_2, value: Constants.show_name.test_2 },
        { id: Constants.show_name.test_3, value: Constants.show_name.test_3 },
        { id: Constants.show_name.test_4, value: Constants.show_name.test_4 }
      ];
    }

    // C-WAGS hosts commonly run many trials per weekend — offer Trial 1-8 for every C-WAGS show type
    if (sanctioningClub === Constants.sanctioning_club.C_WAGS) {
      return [
        Constants.show_name.trial_1,
        Constants.show_name.trial_2,
        Constants.show_name.trial_3,
        Constants.show_name.trial_4,
        Constants.show_name.trial_5,
        Constants.show_name.trial_6,
        Constants.show_name.trial_7,
        Constants.show_name.trial_8,
      ].map((name) => ({ id: name, value: name }));
    }

    if (sanctioningClub === Constants.sanctioning_club.AKC && showType === Constants.show_type.agility) {
      return [
        { id: Constants.show_name.trial_1, value: Constants.show_name.trial_1 },
      ];
    }

    if (sanctioningClub === Constants.sanctioning_club.GENERAL && showType === Constants.show_type.dock_diving) {
      return [
        { id: Constants.show_name.fun_day, value: Constants.show_name.fun_day },
      ];
    }

    if (showType === Constants.show_type.weight_pull) {
      return [
        { id: Constants.show_name.pull_1, value: Constants.show_name.pull_1 },
        { id: Constants.show_name.pull_2, value: Constants.show_name.pull_2 },
      ];
    }

    switch (showType) {
      case Constants.show_type.conformation:
        if (sanctioningClub === Constants.sanctioning_club.AKC || sanctioningClub === Constants.sanctioning_club.ABI) {
          return [
            { id: Constants.show_name.show_1, value: Constants.show_name.show_1 },
            { id: Constants.show_name.show_2, value: Constants.show_name.show_2 },
            { id: Constants.show_name.show_3, value: Constants.show_name.show_3 },
            { id: Constants.show_name.show_4, value: Constants.show_name.show_4 }
          ];
        } else {
          return [
            { id: Constants.show_name.show_1, value: Constants.show_name.show_1 },
            { id: Constants.show_name.show_2, value: Constants.show_name.show_2 }
          ];
        }
    }

    return [
      { id: Constants.show_name.trial_1, value: Constants.show_name.trial_1 },
      { id: Constants.show_name.trial_2, value: Constants.show_name.trial_2 }
    ];
  }

  // const isSecondShow = () => {
  //   return (
  //     showName.value === Constants.show_name.trial_2 ||
  //     showName.value === Constants.show_name.show_2 ||
  //     showName.value === Constants.show_name.trial_3 ||
  //     showName.value === Constants.show_name.show_3 ||
  //     showName.value === Constants.show_name.trial_4 ||
  //     showName.value === Constants.show_name.show_4
  //   );
  // }

  const getShowName = (s: IShow) => {
    return s.show_type + " " + s.show_name + " (" + moment(s.show_date).format("MMM Do") + ")";
  }

  const findPreviousShow = (clubShows: IShow[], show: IShow) => {
    // First, try to find by previous_show_id (most reliable)
    if (show?.previous_show_id) {
      const foundById = clubShows.find(s => s._id === show.previous_show_id);
      if (foundById) {
        return foundById;
      }
    }
    
    // Fallback: match by previous_show_name (for backward compatibility with older data)
    // Find all shows that match the previous_show_name (type + name)
    const matchingShows: IShow[] = [];
    
    for (let i in clubShows) {
      let cshow: IShow = clubShows[i];

      if (show?.previous_show_name?.includes(cshow.show_name)) {
        if (show?.previous_show_name.includes(cshow.show_type)) {
          matchingShows.push(cshow);
        }
      }
    }
    
    // If only one match, return it
    if (matchingShows.length <= 1) {
      return matchingShows[0];
    }
    
    // If multiple matches (same name/type on different dates), find the best one
    // The previous show should be on the same date or closest date before the trailing show
    if (matchingShows.length > 1 && show?.show_date) {
      const trailingShowDate = moment(show.show_date);
      
      // First, try to find a show on the same date
      const sameDateShow = matchingShows.find(s => 
        moment(s.show_date).format('YYYY-MM-DD') === trailingShowDate.format('YYYY-MM-DD')
      );
      
      if (sameDateShow) {
        return sameDateShow;
      }
      
      // Otherwise, find the closest show before the trailing show date
      let closestShow: IShow | undefined;
      let closestDiff = Infinity;
      
      for (const s of matchingShows) {
        const diff = trailingShowDate.diff(moment(s.show_date), 'minutes');
        // Only consider shows before or at the same time as the trailing show
        if (diff >= 0 && diff < closestDiff) {
          closestDiff = diff;
          closestShow = s;
        }
      }
      
      return closestShow || matchingShows[0];
    }
    
    return matchingShows[0];
  }

  const getClubEventShows = useCallback(() => {
    return new Promise((resolve) => {
      let query: any = {
        sort: [{
          attribute_name: "show_type",
          sort: "asc"
        }, {
          attribute_name: "show_date",
          sort: "asc"
        }]
      };
      
      if (props.clubId) {
        apiService.getClubEventShows(props.clubId, props.event._id, query).then((response: any[]) => {
          resolve(response);
        }).catch(() => {
          resolve(showNameTabErrors.NOT_FOUND);
        });
      } else {
        resolve(showNameTabErrors.NO_CLUB);
      }
    });
  }, [props.clubId, props.event._id]);

  // Reset loaded state when show changes to force re-initialization
  useEffect(() => {
    setLoaded(false);
  }, [props.show?._id]);

  useEffect(() => {
    if (!loaded) {
      const init = async() => {
        const hydrateShowNameTab = () => {
          if (props.show) {
            setAkcNum(props.show?.event_number || "");
            setShowName({ id: props.show.show_name, value: props.show.show_name });
          } else {
            const options = getShowNamesByType(props.event.sanctioning_club, props.showType);
            if (options.length > 0) {
              setShowName(options[0]);
            }
          }
          if (props.show) {
            setCustomShowName(props.show.custom_show_name || "");
            if (props.event.timezone) {
              setShowDate(helperService.getDateTimeWithTimezone(props.event.timezone, props.show.show_date));
            } else {
              setShowDate(moment(props.show.show_date).toDate());
            }

            setUseCloseDate(props.show.use_registration_end_date);

            if (props.show.registration_end_date) {
              const closeDateWithTz = props.event.timezone
                ? helperService.getDateTimeWithTimezone(props.event.timezone, props.show.registration_end_date)
                : moment(props.show.registration_end_date).toDate()
              setCloseDate(closeDateWithTz);
            } else {
              setCloseDate(new Date())
            }

            setIsTrailingShow(props.show.trailing_show === true);
          }
        }

        // First hydrate show data so isTrailingShow is set
        if (props.show) {
          hydrateShowNameTab();
        }

        const clubShows:any = await getClubEventShows();

        if (clubShows.length > 0) {
          setEventShows(clubShows);

          if (props.show && props.show.trailing_show) {
            const foundPrevShow = findPreviousShow(clubShows, props.show);
            setActualPreviousShow(foundPrevShow);
            
            // Set the dropdown value to match the format used in dropdown options
            if (foundPrevShow) {
              setPreviousShow({ 
                value: getShowName(foundPrevShow), 
                id: foundPrevShow._id 
              });
            }
          } else {
            // Reset previous show selection if not a trailing show
            setPreviousShow({ value: "Select a Previous Show", id: "Select a Previous Show" });
            setActualPreviousShow(undefined);
          }
        }
        
        // Mark as loaded AFTER all async work is complete
        setLoaded(true);
      }

      init();
    }
  }, [loaded, getClubEventShows, props.show, props.event.timezone]);

  let { onChange } = props;

  useEffect(() => {
    onChange(akcNum, showName.id, showDate, closeDate, useCloseDate, isTrailingShow, actualPreviousShow, customShowName, isFunMatch);
  }, [onChange, akcNum, showName, showDate, closeDate, useCloseDate, isTrailingShow, actualPreviousShow, customShowName, isFunMatch]);

  useEffect(() => {
    const getShowById = (id: string) => {
      for (let s in eventShows) {
        if (eventShows[s]._id === id) {
          return eventShows[s];
        }
      }

      return undefined;
    }

    let show:any = getShowById(previousShow.id);

    if (show) {
      setActualPreviousShow(show);
    }
  }, [previousShow, eventShows]);

  return (
    <div className={styles.ShowNameTab}>
      <div className="stepTitle">Show Name and Date</div>
      <p>Whats the name of your show, and when is it?</p>
      <>{props.event?.sanctioning_club === Constants.sanctioning_club.AKC &&
        props.showType !== Constants.show_type.canine_good_citizen &&
        props.showType !== Constants.show_type.trick_dog &&
        <Input required={!isFunMatch} label="AKC Number" defaultValue={akcNum} id="akcNum" type="text" onChange={(e) => { e.stopPropagation(); setAkcNum(e.target.value); }} />
      }</>
      <>{showFunMatchOption &&
        <div style={{ marginTop: "-12px", marginBottom: "20px" }}>
          <Checkbox
            id="isFunMatch"
            label="This is a fun match (AKC number not required)"
            value={isFunMatch}
            onChange={(e: any) => setIsFunMatch(e.target.checked)}
          />
        </div>
      }</>
      <Dropdown
        value={showName}
        onChange={(e: any, value: IOption) => {
          setShowName(value);
        }}
        label="Show Name"
        options={getShowNamesByType(props.event.sanctioning_club, props.showType)} placeholder="Select a Show Name"
      />
      <>{props.event?.sanctioning_club === Constants.sanctioning_club.AKC && (props.showType === Constants.show_type.conformation || props.show?.show_type === Constants.show_type.conformation) && (
        <Input
          label="Custom Show Name (optional)"
          id="customShowName"
          type="text"
          defaultValue={customShowName}
          onChange={(e) => { e.stopPropagation(); setCustomShowName(e.target.value); }}
          placeholder="e.g. Specialty, Morning Show..."
        />
      )}</>
      <>{eventShows.length > 0 && <div className="trailingShow">
        <Checkbox onChange={(e:any) => { setIsTrailingShow(!isTrailingShow); }} value={isTrailingShow} id="isTrailingShow" label="This Show Follows a Previous Show" />
        {isTrailingShow && <div className="selectTrailingShow">
          <Dropdown value={previousShow} onChange={(e: any, value: IOption) => { setPreviousShow(value); }} label="Previous Show" options={eventShows.filter((s: IShow) => s._id !== props.show?._id).map((s: IShow) => { return { value: getShowName(s), id: s._id } })} placeholder="Choose a Previous Show" />
        </div>}
      </div>}</>
      <>{!isTrailingShow && <DatePick className="startDateTime" label="Show Start Date & Time" onChange={setShowDate} value={showDate} showTimeSelect={true} rightText={props.event.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone } />}</>
      <p>Entries will close at the Show Start Date & Time unless an Entry Close Date is specified. </p>
      <Checkbox onChange={(e:any) => { setUseCloseDate(!useCloseDate); }} value={useCloseDate} id="useCloseDate" label="Use an Entry Close Date" />
      <>{useCloseDate &&
        <div className="earlyBird">
          <p>What day should we close entries on?</p>
          <DatePick label="Entry Close Date" onChange={setCloseDate} value={closeDate} showTimeSelect={true} rightText={props.event.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone } />
        </div>
      }</>
    </div>
  );
};

export default ShowNameTab;